import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Check } from "lucide-react";
import { ASSETS, SPECS, PRODUCT_FEATURES } from "../../data";
import { Reveal, MaskLineInView } from "./Reveal";

export const Product = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="product" data-testid="product-section" className="bg-[var(--surface)] py-16 md:py-36">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <Reveal>
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-[var(--brand)]">
                Flagship Product
              </span>
            </Reveal>
            <h2 className="font-display font-semibold tracking-tighter leading-[0.95] mt-5 text-4xl md:text-6xl">
              <MaskLineInView>ALASKA 27MM</MaskLineInView>
              <MaskLineInView delay={0.08}>Neck HDPE Cap</MaskLineInView>
            </h2>
          </div>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-[var(--muted)] leading-relaxed">
              A compression-molded closure engineered for 28mm PET preforms — premium finish,
              leak-proof seal, and food-grade integrity on every piece.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Reveal className="lg:col-span-7" y={50}>
            <div ref={ref} className="relative h-[380px] md:h-[560px] rounded-3xl overflow-hidden bg-white border border-slate-200">
              <motion.img
                style={{ y }}
                src={ASSETS.capMacro}
                alt="ALASKA 27mm HDPE cap macro"
                className="absolute inset-0 h-[116%] w-full object-cover"
              />
              <div className="absolute top-5 left-5 rounded-full bg-[var(--brand)] px-4 py-1.5 text-xs font-semibold text-white font-mono tracking-wide">
                1.3g · Food-Grade
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-5 grid grid-cols-2 gap-px bg-slate-200 border border-slate-200 rounded-3xl overflow-hidden">
            {SPECS.map((s, i) => (
              <motion.div
                key={s.k}
                data-testid={`spec-${i}`}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="bg-white p-6 md:p-7 flex flex-col justify-between hover:bg-[var(--surface)] transition-colors"
              >
                <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
                  {s.k}
                </span>
                <span className="font-display text-lg md:text-xl font-medium mt-4 tracking-tight leading-tight">
                  {s.v}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PRODUCT_FEATURES.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.06}>
              <div
                data-testid={`feature-${i}`}
                className="h-full rounded-2xl border border-slate-200 bg-white p-7 hover:border-[var(--brand)] hover:-translate-y-1 transition-[transform,border-color] duration-300"
              >
                <div className="w-10 h-10 rounded-full bg-[var(--brand)]/10 flex items-center justify-center text-[var(--brand)]">
                  <Check size={18} strokeWidth={3} />
                </div>
                <h3 className="font-display text-lg font-medium mt-5 tracking-tight">{f.t}</h3>
                <p className="mt-2 text-sm text-[var(--muted)] leading-relaxed">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
